/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {closeModal, closeSecondaryModal, openModal, setHeadbarTitle} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import {useTypedSelector} from 'helpers/reducers'
import BookingAction from 'store/booking/BookingAction'
import {setFilter} from 'store/booking/BookingReducer'
import {OffersAction} from 'store/offers/OffersAction'
import {setDraftOfferId} from 'store/offers/OffersReducer'
import useSellerTimeslots from 'utils/hooks/useSellerTimeslots'
import {toast} from 'utils/toastUtils'
import {ModalSize} from 'types/enums/ModalSize'
import {EditLeadModalTab} from 'types/enums/specific/EditLeadModalTab'
import {CreateOfferStep} from 'components/Offers/enums/CreateOfferStep'
import {OfferBasicInputs} from 'components/Offers/schemas/offerBasicSchema'
import {Offer} from 'types/entities/Offer'
import {Company} from 'types/entities/Company'
import {Lead} from 'types/entities/Lead'
import {useLeadActions} from 'utils/leads/leadsUtils'
import EditOfferModal from 'components/Offers/EditOfferModal'
import {buildCreateOfferSteps, buildOfferBasicDefaults, isOfferLeadDefaultsLoading} from 'components/Offers/utils/createOfferModalUtils'
import Dates from 'utils/Dates'
import {useOfferFlow} from "components/Offers/hooks/useOfferFlow";

interface UseCreateOfferOrchestrationParams {
    onCreated?: (offer?: Offer) => void
    companyId?: number | null
    company?: Company | null
    leadId?: number | null
    preferredTime?: string
    embedded?: boolean
    disableCompanyChange?: boolean
    defaultValues?: Partial<OfferBasicInputs>
    onGuardStateChange?: (offerId: number | null) => void
}

export const useCreateOfferOrchestration = ({
    onCreated,
    companyId,
    company,
    leadId,
    embedded = false,
    defaultValues,
    onGuardStateChange,
}: UseCreateOfferOrchestrationParams) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { openEditLeadModal } = useLeadActions()

    const seller_id = useTypedSelector((state) => state.sessionState.session?.uid)
    const selectedOffer = useTypedSelector((state) => state.offersState.selectedOffer)
    const draftOfferId = useTypedSelector((state) => state.offersState.draftOfferId)
    const selectedLead = useTypedSelector((state) => state.leadsState.selectedLead)
    const packageCart = useTypedSelector((state) => state.bookingState.bookingProcess.packageCart)
    const filter = useTypedSelector((state) => state.bookingState.bookingProcess.filter)
    const selectedPackageItems = useTypedSelector((state) => state.bookingState.bookingProcess.selectedPackageItems)
    const selectedUpsells = useTypedSelector((state) => state.bookingState.bookingProcess.upsell)
    const bookingProcess = useTypedSelector((state) => state.bookingState.bookingProcess)
    const bookingFilter = bookingProcess.filter

    const [activeStep, setActiveStep] = useState<CreateOfferStep>(CreateOfferStep.Details)
    const [maxReachedStep, setMaxReachedStep] = useState<CreateOfferStep>(CreateOfferStep.Details)
    const [isCreatingOffer, setIsCreatingOffer] = useState(false)
    const [offerCreationError, setOfferCreationError] = useState<string | null>(null)
    const [submittedBasicValues, setSubmittedBasicValues] = useState<Partial<OfferBasicInputs> | null>(null)
    const [showResumePrompt, setShowResumePrompt] = useState(false)
    const [isDiscardingDraft, setIsDiscardingDraft] = useState(false)

    const effectiveCompanyId = submittedBasicValues?.company_id ?? companyId ?? null

    const basicFormDefaultValues = useMemo<Partial<OfferBasicInputs> | undefined>(
        () => buildOfferBasicDefaults({ defaultValues, leadId, selectedLead }),
        [defaultValues, leadId, selectedLead]
    )

    const isLeadDefaultsLoading = useMemo(
        () => isOfferLeadDefaultsLoading({ leadId, defaultValues, selectedLead }),
        [leadId, defaultValues, selectedLead]
    )

    const mergedBasicFormDefaultValues = useMemo<Partial<OfferBasicInputs> | undefined>(
        () => ({ ...(basicFormDefaultValues ?? {}), ...(submittedBasicValues ?? {}) }),
        [basicFormDefaultValues, submittedBasicValues]
    )

    const selectedCompanyForForm = useMemo(() => {
        if (!company || !effectiveCompanyId) return null
        return Number(company.id) === Number(effectiveCompanyId) ? company : null
    }, [company, effectiveCompanyId])

    const steps = useMemo(() => buildCreateOfferSteps(t), [t])

	const {isNextDisabled} = useOfferFlow();

    const handleSuccess = useCallback(
        async (offer?: Offer) => {
            if (!offer) return
            dispatch(setDraftOfferId(null))
            await dispatch(OffersAction.enforceTargetCalculation(offer, offer.seller_id))
            if (onCreated) onCreated(offer)

            if (embedded) {
                dispatch(BookingAction.clearBookingProcessState())
                const handleClose = () => {
                    dispatch(closeSecondaryModal())
                    if (leadId) {
                        openEditLeadModal({ id: leadId } as Lead, () => {}, EditLeadModalTab.Offers)
                    }
                }
                dispatch(
                    openModal(
                        <EditOfferModal offerId={offer.id} onClose={handleClose} />,
                        { modalSize: ModalSize.RoundedFullscreen, closeModal: handleClose },
                        t('Offer Detail')
                    )
                )
            } else {
                dispatch(closeModal())
                dispatch(BookingAction.clearBookingProcessState())
                navigate(`/offers/${offer.id}`)
            }
        },
        [dispatch, onCreated, navigate, embedded, leadId, openEditLeadModal, t]
    )

    const { createOfferWithUpsells, isLoading: isUpsellsLoading } = useSellerTimeslots(
        () => {},
        null,
        [null, null, null],
        (offer) => handleSuccess(offer),
        seller_id,
        false,
        true
    ) as any

    const submitPackageUpsells = () => {
        if (!bookingProcess.selectedTimeslot) {
            toast.error(t('No timeslot selected'))
            return
        }
        if (!bookingFilter.category) {
            toast.error(t('No category selected'))
            return
        }
        if (selectedOffer) {
            createOfferWithUpsells(selectedOffer, true)
        } else {
            toast.error(t('No selected offer found'))
        }
    }

    const handlePackageItemsSuccess = useCallback(async () => {
        if (!selectedOffer?.id) {
            setOfferCreationError('Offer was not created. Please try again.')
            setActiveStep(CreateOfferStep.PackageItems)
            return
        }
        setActiveStep(CreateOfferStep.Upsells)
        setMaxReachedStep(CreateOfferStep.Upsells)
    }, [selectedOffer?.id])

    const handleCreateOfferFromFirstStep = async (values: OfferBasicInputs) => {
        if (isCreatingOffer) return

        setSubmittedBasicValues(values)
        setIsCreatingOffer(true)
        setOfferCreationError(null)

        try {
            if (!filter.timeslot_id) {
                setOfferCreationError('Please select a timeslot before continuing.')
                setIsCreatingOffer(false)
                return
            }

            const packageItemIds = selectedPackageItems.map((item) => item.id)
            const hour = filter.hour ?? 0
            const minute = filter.minute ?? 0
            const validUntil = values.valid_until
                ? Dates.dayjs(values.valid_until).format('YYYY-MM-DD HH:mm:ss')
                : null

            const offerCreationPromise = dispatch(
                OffersAction.fetchCreateOrUpdateOffer({
                    id: selectedOffer?.id,
                    seller_id: Number(seller_id),
                    company_id: values.company_id ?? null,
                    lead_id: leadId ?? null,
                    contact_id: values.contact_id ?? null,
                    probability: values.probability ?? null,
                    valid_until: validUntil,
                    packages_cart_id: packageCart?.id ?? null,
                    input: {
                        date: values.date,
                        drivers_count_adult: values.adults || 0,
                        drivers_count_child: values.children || 0,
                        hour,
                        minute,
                        duration_minutes: 0,
                        package_item_ids: packageItemIds,
                    } as any,
                } as any)
            )

            if (offerCreationPromise && typeof offerCreationPromise.then === 'function') {
                await offerCreationPromise
            }

            await new Promise((resolve) => setTimeout(resolve, 200))

            dispatch(setFilter({ ...bookingFilter, adults: values.adults || 0, children: values.children || 0 }))
            setActiveStep(CreateOfferStep.PackageItems)
            setMaxReachedStep(CreateOfferStep.PackageItems)
            setIsCreatingOffer(false)
        } catch (error) {
            console.error('Error creating offer:', error)
            setOfferCreationError('An error occurred while creating the offer')
            setIsCreatingOffer(false)
        }
    }

    const handleResumeDraft = () => {
        if (!draftOfferId) return
        dispatch(OffersAction.fetchOfferById(draftOfferId))
            .then(() => {
                setShowResumePrompt(false)
                setActiveStep(CreateOfferStep.PackageItems)
                setMaxReachedStep(CreateOfferStep.PackageItems)
            })
            .catch(() => {
                dispatch(setDraftOfferId(null))
                dispatch(BookingAction.clearBookingProcessState())
                dispatch(OffersAction.resetSelectedOffer())
                setShowResumePrompt(false)
            })
    }

    const handleDiscardDraft = async () => {
        if (!draftOfferId) return
        setIsDiscardingDraft(true)
        try {
            await dispatch(OffersAction.fetchDeleteOffer(draftOfferId))
        } catch {
            /* ignore */
        } finally {
            dispatch(setDraftOfferId(null))
            dispatch(BookingAction.clearBookingProcessState())
            dispatch(OffersAction.resetSelectedOffer())
            setIsDiscardingDraft(false)
            setShowResumePrompt(false)
        }
    }

    useLayoutEffect(() => {
        if (draftOfferId) {
            setShowResumePrompt(true)
        } else {
            dispatch(BookingAction.clearBookingProcessState())
            dispatch(OffersAction.resetSelectedOffer())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!onGuardStateChange) return
        const isGuarded = activeStep !== CreateOfferStep.Details && !!selectedOffer?.id
        onGuardStateChange(isGuarded ? (selectedOffer?.id ?? null) : null)
    }, [activeStep, selectedOffer?.id, onGuardStateChange])

    useEffect(() => {
        if (!seller_id) return
        dispatch(BookingAction.initSellerCart(true, seller_id))
    }, [dispatch, seller_id])

    useEffect(() => {
        dispatch(setHeadbarTitle(t('Create Offer')))
        return () => { dispatch(setHeadbarTitle(null)) }
    }, [dispatch, t])


	return {
        // state
        activeStep,
        setActiveStep,
        maxReachedStep,
        isCreatingOffer,
        offerCreationError,
        showResumePrompt,
		isNextDisabled,
        isDiscardingDraft,
        isLeadDefaultsLoading,
        isUpsellsLoading,
        // derived
        effectiveCompanyId,
        mergedBasicFormDefaultValues,
        selectedCompanyForForm,
        steps,
        // store refs
        selectedOffer,
        draftOfferId,
        bookingProcess,
        selectedUpsells,
        // handlers
        handleCreateOfferFromFirstStep,
        handlePackageItemsSuccess,
        handleSuccess,
        submitPackageUpsells,
        handleResumeDraft,
        handleDiscardDraft,
    }
}
